import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { KitchenService } from '../../../../services/configurators-2d/applications/kitchen.service';
import { InstallationState, KitchenExtraPricing } from '../../../../stores/tunnel/model/tunnel-data-state';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { KitchenConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay, startWith, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-kitchen-installation',
  templateUrl: './kitchen-installation.component.html',
})
export class KitchenInstallationComponent extends BaseTunnelObject implements OnInit {
  protected tunnelDataStore = inject(TunnelDataStore);
  protected tunnelNavigationStore = inject(TunnelNavigationStore);
  protected kitchenService = inject(KitchenService);
  protected modalService = inject(ModalService);
  private readonly statisticService = inject(StatisticService);
  private destroyRef = inject(DestroyRef);
  protected featureSettingsService = inject(FeatureSettingsService);

  protected currentInstallation$ = this.tunnelDataStore.getCurrentInstallation();
  protected currentPricing$ = this.tunnelDataStore.getCurrentPricing();
  protected currentDiscount$ = this.tunnelDataStore.getCurrentDiscount();
  protected pricingLoading$ = this.tunnelDataStore.getPricingLoading();
  protected installationLoading$ = this.tunnelDataStore.getInstallationLoading();

  protected kitchenConfiguration$ = this.tunnelDataStore.getConfigurationData().pipe(
    map(configurationData => configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData),
    shareReplay(1) // Cache le résultat
  );

  // Observables dérivés pour les getters
  protected extraPricing$ = this.currentPricing$.pipe(map(pricing => pricing?.extraPricing as KitchenExtraPricing));

  protected discountNumberValue$ = this.currentDiscount$.pipe(
    map(discount => {
      switch (discount) {
        case DiscountTypeEnum.Discount0:
          return 0;
        case DiscountTypeEnum.Discount5:
          return 5;
        case DiscountTypeEnum.Discount10:
          return 10;
        case DiscountTypeEnum.Discount20:
          return 20;
        default:
          return 0;
      }
    })
  );

  protected isCustomWorkbenchOnly$ = this.extraPricing$.pipe(
    map(extra => extra?.totalWorkbenchPlan.actualAmount > 0 && !extra?.totalInstallation && !extra?.totalDelivery)
  );

  protected isCustomWorkbench$ = this.extraPricing$.pipe(
    map(
      extra =>
        extra?.totalWorkbenchPlan.actualAmount > 0 &&
        (extra?.totalInstallation !== undefined || extra?.totalDelivery !== undefined)
    )
  );

  protected isInstallationValid$ = combineLatest([this.currentPricing$, this.currentInstallation$]).pipe(
    map(([pricing, installation]) => {
      return pricing?.totalWithoutFunding?.actualAmount > 0 && installation?.detailInstallation !== undefined;
    })
  );

  protected displayTitleWithoutDelta$ = combineLatest([
    this.currentPricing$,
    this.installationLoading$,
    this.pricingLoading$,
  ]).pipe(
    map(([pricing, installationLoading, pricingLoading]) => {
      return pricing === undefined || !pricing?.delta?.actualAmount || installationLoading || pricingLoading;
    })
  );

  protected displayDiscount$ = this.discountNumberValue$.pipe(map(value => value > 0));

  protected installationViewModel$ = combineLatest({
    displayDiscount: this.displayDiscount$,
    displayDiscountValue: this.discountNumberValue$,
    currentPricing: this.currentPricing$,
    currentInstallation: this.currentInstallation$,
    pricingLoading: this.pricingLoading$,
    installationLoading: this.installationLoading$,
    extraPricing: this.extraPricing$,
    isCustomWorkbenchOnly: this.isCustomWorkbenchOnly$,
    isCustomWorkbench: this.isCustomWorkbench$,
    isInstallationValid: this.isInstallationValid$,
    displayTitleWithoutDelta: this.displayTitleWithoutDelta$,
  });

  // Propriétés locales uniquement pour ce qui est vraiment local
  protected setWithInstallationLoading = false;
  private initInstallationLoading = false;

  protected more2YearsOptions = [
    { label: 'yes', value: true },
    { label: 'no', value: false },
  ];
  protected more2YearsInput: boolean;

  async ngOnInit(): Promise<void> {
    if (!(await this.featureSettingsService.isPoseEnabled())) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    super.ngOnInit();
    await this.sendAccessPoseStatistic();
  }

  async sendAccessPoseStatistic() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    this.statisticService.sendStatistic(
      StatisticTypeEnum.KITCHEN_INSTALLATION_COUNT,
      1,
      CONFIG_CONTEXT.KITCHEN,
      kitchenConfig.configurationId
    );
  }

  protected subscribeToObservable() {
    this.currentInstallation$
      .pipe(
        distinctUntilChanged(
          (prev, curr) => prev?.withInstallation === curr?.withInstallation && prev?.more2Years === curr?.more2Years
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async currentInstallation => {
        if (currentInstallation) {
          const poseEnabled = await this.featureSettingsService.isPoseEnabled();
          this.handleInstallationChange(currentInstallation, poseEnabled);
        } else {
          this.initInstallation();
        }
      });
  }

  private async initInstallation() {
    if (this.initInstallationLoading) {
      return;
    }

    try {
      this.initInstallationLoading = true;
      const poseEnabled = await this.featureSettingsService.isPoseEnabled();
      await this.setWithInstallation(poseEnabled, false);
      await this.ensurePricingLoaded();
    } catch (error) {
      logError(error);
    } finally {
      this.initInstallationLoading = false;
    }
  }

  private async ensurePricingLoaded(): Promise<void> {
    const currentPricing = this.tunnelDataStore.getCurrentStateValue().currentPricing;

    if (currentPricing?.totalWithoutFunding !== undefined) {
      return;
    }

    await this.kitchenService.computePricing(true, false);
  }

  private handleInstallationChange(installation: InstallationState, poseEnabled: boolean): void {
    if (!poseEnabled && installation.withInstallation !== false) {
      this.setWithInstallation(false, true);
      return;
    }
    if (!this.setWithInstallationLoading) {
      this.computeInstallation();
    }
    this.more2YearsInput = installation.more2Years;
  }

  public handleOnSelectButtonChange(event: SelectButtonChangeEvent): void {
    if (event.value === null || event.value === undefined) {
      // Force la réaffectation de la valeur actuelle pour annuler la désélection
      setTimeout(() => {
        this.currentInstallation$.pipe(take(1)).subscribe(installation => {
          this.more2YearsInput = installation.more2Years;
        });
      });
    } else {
      this.setMore2Years(event.value);
    }
  }

  openDetail() {
    this.currentInstallation$
      .pipe(
        take(1) // Important : prend seulement la valeur actuelle et se désabonne
      )
      .subscribe(installation => {
        this.modalService.display(MODAL.INSTALLATION_DETAIL, {
          installationDetails: installation.detailInstallation,
          bom: this.tunnelDataStore
            .getCurrentStateValue()
            .configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData,
        });
      });
  }

  openDetailWorkbench() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;
    const pricing = state.currentPricing;

    this.modalService.display(MODAL.WORKBENCH_DETAIL, {
      workbenchResume: (pricing?.extraPricing as KitchenExtraPricing)?.kitchenWorkbenchConfigurations,
      bom: kitchenConfig?.bom,
      discountValue: state.currentDiscount,
    });
  }

  protected async setMore2Years(more2Years: boolean, withCompute: boolean = true): Promise<void> {
    if (more2Years === null) {
      return;
    }
    try {
      this.tunnelDataStore.setMoreThan2Years(more2Years);
      this.tunnelDataStore.resetCurrentFunding();

      if (withCompute) {
        await this.computeInstallation();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de more2Years:', error);
    }
  }

  protected async setWithInstallation(withInstallation: boolean, computeInstallation: boolean = true): Promise<void> {
    try {
      this.setWithInstallationLoading = true;
      this.tunnelDataStore.setWithInstallation(withInstallation);
      this.tunnelDataStore.resetCurrentFunding();

      await this.kitchenService.updateWithInstallation(withInstallation);

      if (computeInstallation) {
        this.tunnelDataStore.setMoreThan2Years(this.more2YearsInput ?? false);
        await this.computeInstallation();
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'installation:", error);
    } finally {
      this.setWithInstallationLoading = false;
    }
  }

  private async computeInstallation() {
    try {
      await this.kitchenService.computeInstallation();
    } catch (error) {
      logError(error);
    }
  }
}
